export type MonitoringRuntime = 'client' | 'server' | 'edge';

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
const DEFAULT_PROFILES_SAMPLE_RATE = 0;
const DEFAULT_REPLAY_SESSION_SAMPLE_RATE = 0.02;
const DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE = 1;

function parseBool(value: string | undefined, fallback: boolean) {
  if (value === undefined) return fallback;
  return value === 'true';
}

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getMonitoringProvider(runtime?: MonitoringRuntime) {
  const clientProvider = process.env.NEXT_PUBLIC_ERROR_MONITOR_PROVIDER;
  const serverProvider = process.env.ERROR_MONITOR_PROVIDER;

  if (runtime === 'client') return clientProvider;
  if (runtime === 'server' || runtime === 'edge') {
    return serverProvider || clientProvider;
  }

  return typeof window === 'undefined' ? serverProvider || clientProvider : clientProvider;
}

export function getSentryDsn(runtime?: MonitoringRuntime) {
  const publicDsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const serverDsn = process.env.SENTRY_DSN;

  if (runtime === 'client') return publicDsn;
  return serverDsn || publicDsn;
}

export function isSentryEnabled(runtime?: MonitoringRuntime) {
  return getMonitoringProvider(runtime) === 'sentry' && !!getSentryDsn(runtime);
}

export function getSentryInitOptions(runtime: MonitoringRuntime) {
  if (!isSentryEnabled(runtime)) return null;

  const dsn = getSentryDsn(runtime);
  if (!dsn) return null;

  const tracesSampleRate = parseSampleRate(
    process.env.SENTRY_TRACES_SAMPLE_RATE,
    DEFAULT_TRACES_SAMPLE_RATE
  );
  const profilesSampleRate = parseSampleRate(
    process.env.SENTRY_PROFILES_SAMPLE_RATE,
    DEFAULT_PROFILES_SAMPLE_RATE
  );
  const replaysSessionSampleRate = parseSampleRate(
    process.env.SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    DEFAULT_REPLAY_SESSION_SAMPLE_RATE
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    process.env.SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE
  );
  const replayMaskAllText = parseBool(process.env.SENTRY_REPLAY_MASK_ALL_TEXT, true);
  const replayBlockAllMedia = parseBool(process.env.SENTRY_REPLAY_BLOCK_MEDIA, true);

  return {
    dsn,
    enabled: true,
    environment: process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: process.env.SENTRY_RELEASE,
    tracesSampleRate,
    profilesSampleRate,
    replaysSessionSampleRate: runtime === 'client' ? replaysSessionSampleRate : undefined,
    replaysOnErrorSampleRate: runtime === 'client' ? replaysOnErrorSampleRate : undefined,
    replayMaskAllText: runtime === 'client' ? replayMaskAllText : undefined,
    replayBlockAllMedia: runtime === 'client' ? replayBlockAllMedia : undefined,
    debug: process.env.SENTRY_DEBUG === 'true',
  };
}
